/**
 * Who the server thinks we are.
 *
 * Everything here is the server's answer, not the client's belief. Roles live
 * in the database, and whether `X-Act-As` is being honoured is decided per
 * request — so a client that trusted its own `localStorage` could show a banner
 * for an impersonation the server is refusing.
 *
 * Degrades to `null` rather than throwing, like every module in here: with no
 * server configured, or nobody signed in, there is simply no admin bar.
 */

import { setPass } from "../lib/pass";
import { requestHeaders } from "../lib/user";
import { httpBase } from "./backend";

const BASE = httpBase(import.meta.env.VITE_QV_SET_URL);

/** A `grp-*` group's own account, as the Admin As picker lists it. */
export interface GroupAccount {
  /** The account's key — what `X-Act-As` carries. */
  key: string;
  /** The group it publishes to, e.g. `grp-jp`. */
  group: string;
  /** The group's display name, e.g. `JP`. */
  name: string;
}

export interface Me {
  /** Whose data this request would read — the acted-as account, or your own. */
  key: string;
  /** Who is actually signed in. Differs from `key` only while acting as. */
  actorKey: string;
  /** The signed-in person's email, for the menu. Absent from an older server. */
  email?: string | null;
  /** Their name, from the sign-in provider. Usually absent: nothing sets it yet. */
  displayName?: string | null;
  isAdmin: boolean;
  /** Non-null only when the server is honouring an `X-Act-As`. */
  actingAs: string | null;
  /**
   * The group a Publish goes to: set only while acting as that group's own
   * account (`docs/plans/group-accounts.md` §1), `null` everywhere else.
   */
  publishTo: string | null;
  /** The accounts this admin may act as. Test users only, per D31. */
  testUsers: string[];
  /**
   * Admin As: each group's own account, which this admin may author in. Empty
   * for anyone who is not an admin (`docs/plans/group-accounts.md` §1).
   */
  groupAccounts: GroupAccount[];

  /** The plan code this account is on. Empty when entitlements are permissive. */
  plan: string;
  /** The clubs this account belongs to, by key. */
  orgs: string[];
  /**
   * The groups whose layouts this account receives: `orgs`, plus — for a
   * virtual group's member — every group it includes
   * (`docs/plans/group-sharing.md`): `["grp-jas-jp", "grp-jas", "grp-jp"]`.
   * `orgs` from an older server that does not send it.
   */
  memberOf: string[];
  /**
   * Each virtual group this account belongs to, with its includes —
   * `{"grp-jas-jp": ["grp-jas", "grp-jp"]}`. Empty otherwise.
   */
  virtualGroups: Record<string, string[]>;
  /**
   * **Nothing has been decided on this deployment**, so everything is allowed.
   *
   * Not the same as holding every capability by grant, and the difference is
   * what a Settings screen needs: a developer's box and the test suite both
   * report this, and a screen that read it as "you are on the top tier" would
   * invent a plan the server has never heard of.
   */
  permissive: boolean;
  /**
   * The resolved merge — plan, clubs and per-user grants already unioned.
   *
   * **Not authority.** Every gated route checks again, because a list the
   * browser was handed is a list the browser can edit. This exists so a control
   * can be greyed without a round trip, not so the client can decide.
   */
  capabilities: string[];
  /** The numeric ceilings, by capability. A missing one means no ceiling. */
  limits: Record<string, number>;
}

let inFlight: Promise<Me | null> | null = null;

/**
 * Ask the server who we are. **Concurrent callers share one request.**
 *
 * This is sign-in on the server now: it stamps `last_seen_at`, backfills the
 * default group, and signs a fresh groups pass (`docs/plans/auth-hot-path.md`).
 * Six components mount and ask in the same page load; six requests would be
 * six sign-ins.
 */
export function fetchMe(): Promise<Me | null> {
  if (inFlight) return inFlight;
  inFlight = askMe().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

async function askMe(): Promise<Me | null> {
  if (!BASE) return null;
  try {
    const response = await fetch(`${BASE}/auth/me`, { headers: await requestHeaders() });
    if (!response.ok) return null;
    const body = (await response.json()) as Partial<Me> & { pass?: string | null };
    // `null` when the server has no signing key: requests then go without one
    // and the server answers them from its database, as it always did.
    setPass(body.pass ?? null);
    return {
      key: String(body.key ?? ""),
      actorKey: String(body.actorKey ?? ""),
      email: typeof body.email === "string" && body.email ? body.email : null,
      displayName: typeof body.displayName === "string" && body.displayName ? body.displayName : null,
      isAdmin: Boolean(body.isAdmin),
      actingAs: body.actingAs ?? null,
      // Absent from an older server, which means no publish at all rather than
      // falling back to guessing a club from `orgs`.
      publishTo: typeof body.publishTo === "string" ? body.publishTo : null,
      testUsers: Array.isArray(body.testUsers) ? body.testUsers : [],
      groupAccounts: Array.isArray(body.groupAccounts) ? body.groupAccounts : [],
      plan: String(body.plan ?? ""),
      orgs: Array.isArray(body.orgs) ? body.orgs : [],
      // Named, or it vanishes: without it a virtual group's member is "in"
      // grp-jas-jp alone and receives none of JAS's or JP's layouts.
      memberOf: Array.isArray(body.memberOf)
        ? body.memberOf
        : Array.isArray(body.orgs)
          ? body.orgs
          : [],
      virtualGroups: toVirtualGroups(body.virtualGroups),
      // **Absent means permissive**, which is what an older server that predates
      // entitlements sends — it has no opinion, and treating silence as "you
      // hold nothing" would black out every gated control against a backend
      // that is simply behind. The same default the server itself takes.
      permissive: body.permissive ?? true,
      capabilities: Array.isArray(body.capabilities) ? body.capabilities : [],
      limits:
        body.limits && typeof body.limits === "object"
          ? (body.limits as Record<string, number>)
          : {},
    };
  } catch {
    return null;
  }
}

/** `virtualGroups` off the wire: string lists by key, anything else dropped. */
function toVirtualGroups(raw: unknown): Record<string, string[]> {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  const out: Record<string, string[]> = {};
  for (const [key, includes] of Object.entries(raw as Record<string, unknown>)) {
    if (Array.isArray(includes)) {
      out[key] = includes.filter((each): each is string => typeof each === "string");
    }
  }
  return out;
}

/**
 * What the menu calls the signed-in person: their name, else the part of the
 * email before the "@", else a plain "Signed in". Never the account key —
 * that is for Admin › Accounts and the hover text, not for reading.
 */
export function accountLabel(me: Pick<Me, "email" | "displayName"> | null): string {
  const name = me?.displayName?.trim();
  if (name) return name;
  const local = me?.email?.split("@")[0];
  return local || "Signed in";
}

/** The hover text behind `accountLabel`: the full email and the key. */
export function accountTitle(me: Pick<Me, "email" | "actorKey"> | null): string {
  return [me?.email, me?.actorKey].filter(Boolean).join(" · ");
}
